import { cn } from '@amore/ui/cn'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef } from 'react'

export type SectionTab = { to: string; label: string; count?: number; disabled?: boolean }

/**
 * A module's sub-pages (Seznam hostů · Odpovědi z webu · Nastavení): quiet
 * underlined tabs that form the header's dividing line, each tab its own URL.
 * Pass it to `Screen` as `tabs`. The active tab is wine with a wine underline;
 * a count shows only when something waits for the user. One optional action
 * on the right.
 */
export function SectionTabs({
  label,
  tabs,
  active,
  action,
}: {
  label: string
  tabs: SectionTab[]
  active: string
  action?: ReactNode
}) {
  const navigation = useRef<HTMLElement>(null)
  useEffect(() => {
    const nav = navigation.current
    if (!nav) return
    const tab = Array.from(nav.querySelectorAll('a')).find((link) => link.pathname === active)
    if (!tab) return
    const keepVisible = () => {
      const item = tab.getBoundingClientRect()
      const viewport = nav.getBoundingClientRect()
      if (item.left < viewport.left) nav.scrollLeft += item.left - viewport.left
      else if (item.right > viewport.right) nav.scrollLeft += item.right - viewport.right
    }
    keepVisible()
    const observer = new ResizeObserver(keepVisible)
    observer.observe(nav)
    return () => observer.disconnect()
  }, [active])
  return (
    <div className="flex min-h-11 items-end justify-between gap-3 border-b border-border">
      <nav
        ref={navigation}
        aria-label={label}
        className="-mb-px flex max-w-full gap-7 overflow-x-auto"
      >
        {tabs.map((tab) => {
          const current = tab.to === active
          const className = cn(
            'inline-flex shrink-0 items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            current
              ? 'border-action text-action'
              : tab.disabled
                ? 'border-transparent text-muted-foreground/60'
                : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
          )
          const content = (
            <>
              {tab.label}
              {tab.count ? (
                <span className="grid h-5 min-w-5 place-items-center rounded-full bg-action px-1.5 text-[11px] leading-none text-action-foreground tabular-nums">
                  {tab.count}
                </span>
              ) : null}
            </>
          )
          if (tab.disabled)
            return (
              <span key={tab.to} aria-disabled="true" className={className}>
                {content}
              </span>
            )
          return (
            <Link
              key={tab.to}
              to={tab.to}
              activeOptions={{ exact: true }}
              aria-current={current ? 'page' : undefined}
              className={className}
            >
              {content}
            </Link>
          )
        })}
      </nav>
      {action ? <div className="shrink-0 pb-2">{action}</div> : null}
    </div>
  )
}
